import React from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { cn } from '@/lib/utils';

export function SettingsPage() {
  const [activeTab, setActiveTab] = React.useState('profile');

  const navItemClass = (id: string) => cn(
    "px-6 py-3 rounded-lg font-label-caps text-label-caps uppercase tracking-widest text-left transition-all",
    activeTab === id 
      ? "bg-primary text-on-primary shadow-md" 
      : "text-on-surface-variant hover:bg-surface-container-high"
  );

  return (
    <AppLayout>
      <div className="flex-1 bg-surface-container-low overflow-y-auto">
        <header className="bg-surface border-b border-outline-variant px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto">
             <span className="font-label-caps text-label-caps text-secondary uppercase tracking-widest mb-2 block">Configurations</span>
             <h1 className="font-h1 text-h1 text-on-surface mb-4">Forge Settings</h1>
             <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl">
               Personalize your editorial environment and account preferences. These settings dictate the behavior of the Scriptorium Engine.
             </p>
          </div>
        </header>

        <main className="px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Sidebar Nav */}
            <nav className="flex flex-col gap-1 shrink-0">
               <button onClick={() => setActiveTab('profile')} className={navItemClass('profile')}>Profile</button>
               <button onClick={() => setActiveTab('typography')} className={navItemClass('typography')}>Typography</button>
               <button onClick={() => setActiveTab('archives')} className={navItemClass('archives')}>Archives</button>
               <button onClick={() => setActiveTab('ai')} className={navItemClass('ai')}>AI Engine</button>
            </nav>

            {/* Content Area */}
            <div className="lg:col-span-2 space-y-12 pb-24">
               {activeTab === 'profile' && (
                 <section className="bg-surface border border-outline-variant rounded-2xl p-8 md:p-12 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="flex items-center gap-8 pb-8 border-b border-outline-variant">
                      <div className="w-24 h-24 rounded-full border-2 border-primary overflow-hidden p-1 shadow-xl">
                         <img 
                           src="https://lh3.googleusercontent.com/aida-public/AB6AXuDssxiIHblDQLODLe3DnzW-7NG2fcsqPhRSOGcMSI7XfzwJwVS6laJlmhImJu_6rMEXHPq1T4vLsZQ-lZrF25urh4OREK0qSEodZw8RbtWI_KYSFt86lqv7YQH1CuxfZOmfh30cEbZ80rZEW_evi2sFn8DigrrxANsveDUX7xotu_G_h0z_63JcBHQG9UhJxrQhQh6n-qyE0srGSyHewjKJWG9t3h_D7K_n7W6n4H5OaKVaP1EDGiEsPCUgtA9C7JuwaCVWN1E4FJhe" 
                           alt="Marcus Thorne" 
                           className="w-full h-full object-cover rounded-full"
                         />
                      </div>
                      <div>
                        <h2 className="font-h2 text-h2 text-[28px] mb-1">Marcus Thorne</h2>
                        <p className="font-label-caps text-label-caps text-primary uppercase tracking-widest">Master Editor · Epoch III</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                         <label className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-widest block">Pseudonym</label>
                         <input 
                           type="text" 
                           defaultValue="Marcus Thorne" 
                           className="w-full bg-surface-container-low border border-outline-variant rounded py-3 px-4 font-body-md text-on-surface focus:outline-none focus:ring-1 focus:ring-primary"
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-widest block">Email Address</label>
                         <input 
                           type="email" 
                           defaultValue="thorne@docforge.ai" 
                           className="w-full bg-surface-container-low border border-outline-variant rounded py-3 px-4 font-body-md text-on-surface focus:outline-none focus:ring-1 focus:ring-primary"
                         />
                      </div>
                    </div>

                    <button className="bg-primary text-on-primary px-8 py-3 rounded font-label-caps text-label-caps uppercase tracking-widest shadow-md hover:shadow-primary/20 transition-all">
                      Update Personnel Record
                    </button>
                 </section>
               )}

               {activeTab === 'ai' && (
                 <section className="bg-surface border border-outline-variant rounded-2xl p-8 md:p-12 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="space-y-1">
                      <h2 className="font-h2 text-h2 text-[24px]">AI Scribe Behavior</h2>
                      <p className="font-body-md text-body-md text-on-surface-variant italic">Calibrate the intelligence of the forge engine.</p>
                    </div>

                    <div className="space-y-6">
                       {[
                         { label: 'High-Density Prose', desc: 'Prioritize complex terminology and academic structures.', enabled: true },
                         { label: 'Auto-Refine Manuscripts', desc: 'Allow the scribe to automatically correct minor grammatical inconsistencies.', enabled: false },
                         { label: 'Legacy Epoch Compatibility', desc: 'Ensure generated documents match historical formatting standards.', enabled: true }
                       ].map(pref => (
                         <div key={pref.label} className="flex items-center justify-between py-4 border-b border-outline-variant last:border-0">
                            <div className="space-y-1">
                              <h4 className="font-label-caps text-[12px] text-on-surface uppercase tracking-wider">{pref.label}</h4>
                              <p className="font-body-md text-[13px] text-on-surface-variant leading-tight">{pref.desc}</p>
                            </div>
                            <div className={cn(
                              "w-12 h-6 rounded-full relative cursor-pointer transition-colors",
                              pref.enabled ? "bg-primary" : "bg-outline-variant"
                            )}>
                               <div className={cn(
                                 "absolute top-1 w-4 h-4 rounded-full bg-white transition-all",
                                 pref.enabled ? "left-7" : "left-1"
                               )}></div>
                            </div>
                         </div>
                       ))}
                    </div>
                 </section>
               )}

               {activeTab === 'typography' && (
                 <section className="bg-surface border border-outline-variant rounded-2xl p-8 md:p-12 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                   <div className="space-y-1">
                     <h2 className="font-h2 text-h2 text-[24px]">Typography Presets</h2>
                     <p className="font-body-md text-body-md text-on-surface-variant italic">Define the default font families and scale for your manuscripts.</p>
                   </div>
                   
                   <div className="space-y-6 pt-4">
                      <div className="space-y-3">
                        <label className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-widest block">Primary Display Font</label>
                        <select className="w-full bg-surface-container-low border border-outline-variant rounded py-3 px-4 font-body-md text-on-surface">
                           <option>Playfair Display</option>
                           <option>Source Serif 4</option>
                           <option>JetBrains Mono</option>
                        </select>
                      </div>
                      <div className="space-y-3">
                        <label className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-widest block">Standard Body Font</label>
                        <select className="w-full bg-surface-container-low border border-outline-variant rounded py-3 px-4 font-body-md text-on-surface">
                           <option>Source Serif 4</option>
                           <option>Inter</option>
                           <option>Roboto</option>
                        </select>
                      </div>
                   </div>
                 </section>
               )}

               {activeTab === 'archives' && (
                 <section className="bg-surface border border-outline-variant rounded-2xl p-8 md:p-12 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                   <div className="space-y-1">
                     <h2 className="font-h2 text-h2 text-[24px]">Archival Storage</h2>
                     <p className="font-body-md text-body-md text-on-surface-variant italic">Manage the preservation lifecycle of your digital works.</p>
                   </div>
                   
                   <div className="p-8 bg-surface-container-low rounded-xl border border-outline-variant flex items-center justify-between">
                      <div className="space-y-1">
                         <span className="font-label-caps text-[12px] text-on-surface uppercase tracking-wider block">Automatic Snapshot</span>
                         <p className="font-body-md text-[13px] text-on-surface-variant">Store a copy every 500 characters changed.</p>
                      </div>
                      <div className={cn(
                        "w-12 h-6 rounded-full relative cursor-pointer transition-colors bg-primary"
                      )}>
                        <div className="absolute top-1 left-7 w-4 h-4 rounded-full bg-white transition-all"></div>
                      </div>
                   </div>
                 </section>
               )}
            </div>
          </div>
        </main>
      </div>
    </AppLayout>
  );
}
